import React, { useState } from 'react';
import { X, Mail, Lock, ShieldCheck, Loader2, ArrowRight } from 'lucide-react';
import { authApi } from '../api';
import { toast } from '../hooks/use-toast';

/**
 * Two-step password recovery modal opened from the LoginGate "Forgot password?" link.
 * Step 1: user enters email + new password + confirm. Backend emails a 6-digit code.
 * Step 2: user enters the 6-digit code. Backend applies the new password.
 */
const ForgotPasswordModal = ({ open, onClose }) => {
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);

  const reset = () => {
    setStep(1); setEmail(''); setNewPassword(''); setConfirm(''); setCode(''); setLoading(false);
  };

  const close = () => { reset(); onClose && onClose(); };

  if (!open) return null;

  const submitStep1 = async (e) => {
    e.preventDefault();
    if (!email.includes('@')) { toast({ title: 'Invalid email', variant: 'destructive' }); return; }
    if (newPassword.length < 6) { toast({ title: 'Password too short (min 6 characters)', variant: 'destructive' }); return; }
    if (newPassword !== confirm) { toast({ title: 'Passwords do not match', variant: 'destructive' }); return; }
    setLoading(true);
    try {
      await authApi.passwordResetRequest({ email, new_password: newPassword, confirm_password: confirm });
      toast({ title: 'Verification code sent!', description: 'Check your inbox for the 6-digit code.' });
      setStep(2);
    } catch (err) {
      toast({ title: 'Error', description: err?.response?.data?.detail || 'Request failed', variant: 'destructive' });
    } finally { setLoading(false); }
  };

  const submitStep2 = async (e) => {
    e.preventDefault();
    if (code.length !== 6) { toast({ title: 'Enter the 6-digit code', variant: 'destructive' }); return; }
    setLoading(true);
    try {
      await authApi.passwordResetConfirm({ email, code });
      toast({ title: 'Password reset successful!', description: 'You can now sign in with your new password.' });
      close();
    } catch (err) {
      toast({ title: 'Error', description: err?.response?.data?.detail || 'Invalid or expired code', variant: 'destructive' });
    } finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm px-4" data-testid="forgot-password-modal">
      <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl p-6 relative">
        <button onClick={close} className="absolute top-3 right-3 p-1.5 rounded-full hover:bg-slate-100" data-testid="forgot-close-btn">
          <X className="w-4 h-4 text-slate-500" />
        </button>
        <div className="flex items-center gap-2 mb-4">
          <ShieldCheck className="w-5 h-5 text-blue-600" />
          <h3 className="text-lg font-black text-slate-900">
            {step === 1 ? 'Reset your password' : 'Enter verification code'}
          </h3>
        </div>

        {step === 1 && (
          <form onSubmit={submitStep1} className="space-y-3" data-testid="forgot-step-1">
            <div className="relative">
              <Mail className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Your Gmail address"
                className="w-full pl-10 pr-3 py-2.5 border border-slate-200 rounded-xl text-[14px] focus:outline-none focus:border-blue-400"
                data-testid="forgot-email-input"
                required
              />
            </div>
            <div className="relative">
              <Lock className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="New password (min 6 characters)"
                className="w-full pl-10 pr-3 py-2.5 border border-slate-200 rounded-xl text-[14px] focus:outline-none focus:border-blue-400"
                data-testid="forgot-new-password-input"
                required
              />
            </div>
            <div className="relative">
              <Lock className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                placeholder="Confirm new password"
                className="w-full pl-10 pr-3 py-2.5 border border-slate-200 rounded-xl text-[14px] focus:outline-none focus:border-blue-400"
                data-testid="forgot-confirm-password-input"
                required
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              data-testid="forgot-send-code-btn"
              className="w-full flex items-center justify-center gap-2 rounded-xl py-3 font-black text-white text-[14px] tracking-wider shadow-lg disabled:opacity-60"
              style={{ background: 'linear-gradient(90deg, #1d4ed8, #3b82f6)' }}
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>SEND VERIFICATION CODE <ArrowRight className="w-4 h-4" /></>}
            </button>
          </form>
        )}

        {step === 2 && (
          <form onSubmit={submitStep2} className="space-y-3" data-testid="forgot-step-2">
            <div className="text-[13px] text-slate-600">
              We sent a 6-digit code to <strong className="text-slate-900">{email}</strong>. Enter it below to confirm your new password.
            </div>
            <input
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, '').slice(0, 6))}
              placeholder="6-digit code"
              className="w-full text-center tracking-[0.5em] text-2xl font-black text-slate-900 py-3 border border-slate-200 rounded-xl focus:outline-none focus:border-blue-400"
              data-testid="forgot-code-input"
              maxLength={6}
              required
            />
            <button
              type="submit"
              disabled={loading || code.length !== 6}
              data-testid="forgot-confirm-btn"
              className="w-full flex items-center justify-center gap-2 rounded-xl py-3 font-black text-white text-[14px] tracking-wider shadow-lg disabled:opacity-60"
              style={{ background: 'linear-gradient(90deg, #059669, #10b981)' }}
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>CONFIRM & RESET PASSWORD <ArrowRight className="w-4 h-4" /></>}
            </button>
            <button
              type="button"
              onClick={() => setStep(1)}
              className="w-full text-center text-[12px] text-slate-500 hover:text-slate-700 mt-1"
              data-testid="forgot-back-btn"
            >
              ← Back to email
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default ForgotPasswordModal;
